import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { IDLE, PENDING, ADD } from "const"
import { SilSafetyFunction } from "server/models"
import {
    StateType,
    useInput,
    hasProjectAccess,
} from "client/utils"
import {
    PRM_SIL_SAFETY_FUNCTION,
    StoreType,
    SilSafetyFunctionValidation,

    GetMaxSilSafetyFunctions,
    LoadSilSafetyFunctionById,
    SaveSilSafetyFunction,
    SaveSafetyFunction,
    SaveSilSubsystems,

    setSilSafetyFunctionReloadList,
} from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownItem,
    Input,
    Empty,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

let isMounted: boolean

interface CopyProps {
    safetyFunction: SilSafetyFunction
    show: boolean
    onClose: () => void
}

const Copy = ({
    safetyFunction,
    show,
    onClose,
}: CopyProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectList = useSelector((state: StoreType) => state.project.projectList)
    const projectIds = useSelector((state: StoreType) => state.project.projectIds)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [projectId, setProjectId] = useState<number>(project.id)
    const [newName, setNewName] = useInput("")

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [validation, setValidation] = useState<SilSafetyFunctionValidation>(null)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const copySafetyFunction = () => {
        setSaveState(PENDING)

        LoadSilSafetyFunctionById(safetyFunction.id, safetyFunction.projectId).then((resSafetyFunction) => {
            const originFunction = { ...resSafetyFunction }
            originFunction.copy += 1

            delete originFunction.id
            delete originFunction.createdAt
            delete originFunction.updatedAt

            const name = newName.trim().length > 0 ? newName.trim() : null
            const newFunction = {
                ...originFunction,
                projectId,
                name: name || `${originFunction.name} (${originFunction.copy})`,
                copy: 0,
                calculated: false,
            }

            // max number of safety functions
            const { maxSafetyFunctions } = GetMaxSilSafetyFunctions(packageSettings, userData)

            SaveSilSafetyFunction(null, newFunction, maxSafetyFunctions, lang).then((res) => {
                if (isMounted && res.validation) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                    return
                }

                Promise.all([
                    SaveSafetyFunction(resSafetyFunction.id, originFunction),
                    SaveSilSubsystems(
                        projectId,
                        res.newSafetyFunctionId,
                        originFunction.subsystems,
                    ),
                ]).then(() => {
                    if (isMounted) setSaveState(IDLE)
                    onClose()

                    dispatch(setSilSafetyFunctionReloadList(false))
                    setTimeout(() => {
                        dispatch(setSilSafetyFunctionReloadList(true))
                    }, 0)
                })
            })
        })
    }

    const selectedProject = projectId ? projectList[projectId] : null

    const addAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, ADD, projectId)

    return (
        <ConfirmWrapper show={show} onClose={onClose}>
            <Confirm>
                <ConfirmTitle>{lang.copySafetyFunction}</ConfirmTitle>

                <Row>
                    <Col xs={12}>
                        <FormGroup>
                            <Label>{lang.project}</Label>
                            <SelectDropdown title={selectedProject ? selectedProject.name : <Empty />}>
                                {
                                    projectIds.map((id) => (
                                        <SelectDropdownItem
                                            key={id}
                                            onClick={() => {
                                                setProjectId(id)
                                                setValidation(null)
                                            }}
                                        >
                                            {projectList[id].name}
                                        </SelectDropdownItem>
                                    ))
                                }
                            </SelectDropdown>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.newName}</Label>
                            <Input type="text" value={newName} onChange={setNewName} placeholder={lang.optionalField} />
                            <Feedback>{!addAccess && lang.noAccessToAddSafetyFunction}</Feedback>
                            <Feedback>{validation && validation.name}</Feedback>
                        </FormGroup>
                    </Col>
                </Row>

                <ConfirmBottom>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                    <Button
                        variant="success"
                        row
                        disabled={!addAccess || !projectId}
                        onClick={copySafetyFunction}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.confirm}
                    </Button>
                </ConfirmBottom>
            </Confirm>
        </ConfirmWrapper>
    )
}
export default Copy
